import { themeBoxShadow, themeColor } from '@config/theme';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { mdiMagnify } from '@mdi/js';
import Icon from '@mdi/react';
import { Avatar, Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useUser } from 'services/users';
import { useChannel } from '../hooks/useChat';
import { useChatStore } from '../store';
import { convertApiChannelToLocalModel } from '../utils/channelConversion';
import { getFullUrl } from '../ChatWindow/utils/messageUtils';

interface ChatWindowHeaderProps {
	onSearchClick?: () => void;
}

export const ChatWindowHeader: React.FC<ChatWindowHeaderProps> = ({ onSearchClick }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { selectedChannelId, setSelectedChannelId } = useChatStore();
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	// Use SWR hook for channel data
	const { channel: apiChannel } = useChannel(selectedChannelId || '');

	const { data: user } = useUser();
	// Convert API channel to local model
	const selectedChannel = useMemo(() => {
		if (!apiChannel || !user) return null;
		return convertApiChannelToLocalModel(apiChannel, user.user_id);
	}, [apiChannel, user]);

	// Get the other user from the channel if it's direct messaging
	const otherUser = useMemo(() => {
		if (!selectedChannel || !user || !apiChannel) return null;
		if (selectedChannel.isDirectMessaging()) {
			const otherApiUser = apiChannel.users.find((u) => u.id !== user.user_id);
			if (otherApiUser) {
				return {
					id: otherApiUser.id,
					name: otherApiUser.name || '',
					surname: otherApiUser.surname || '',
					avatar: otherApiUser.avatar || '',
					isOnline: otherApiUser.onlineStatus === 1,
				};
			}
		}
		return null;
	}, [selectedChannel, user, apiChannel]);

	// Handle back button click
	const handleBackClick = () => {
		setSelectedChannelId(null);
		// Also clear the URL parameters to prevent the channel from being selected again
		searchParams.delete('channelId');
		searchParams.delete('channel');
		setSearchParams(searchParams);
	};

	if (!selectedChannel) return null;

	const isDirectChannel = selectedChannel.isDirectMessaging();
	const channelTitle =
		isDirectChannel && otherUser ? `${otherUser.name} ${otherUser.surname}` : selectedChannel.name;

	// Get the avatar URL using getFullUrl with null safety
	const avatarPath = isDirectChannel && otherUser ? otherUser.avatar : selectedChannel.image;
	const avatarSrc = avatarPath ? getFullUrl(avatarPath) : '';

	return (
		<Box
			boxShadow={themeBoxShadow.main}
			minHeight={'72px'}
			width={'100%'}
			display="flex"
			alignItems="center"
			justifyContent="space-between"
			bgcolor={themeColor.veryLightGray}
			flexDirection={{ xs: 'column', sm: 'row' }}
			paddingX={{ xs: 0, sm: 2 }}
		>
			<Box
				display="flex"
				alignItems="center"
				justifyContent="space-between"
				width="100%"
				my={{ xs: 1, sm: 0 }}
			>
				<Box className="flex items-center">
					{isMobile && (
						<IconButton onClick={handleBackClick} aria-label="back" sx={{ mr: 1 }}>
							<ArrowBackIcon />
						</IconButton>
					)}
					<Box
						sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
						className="!flex !justify-self-start"
					>
						<Avatar src={avatarSrc} sx={{ width: 50, height: 50, mr: 2 }} />
						<Box flex={1}>
							<Typography variant="h6" fontSize={16}>
								{channelTitle}
							</Typography>
							{isDirectChannel && otherUser && (
								<Typography
									variant="body2"
									color={themeColor.stormGray}
									display="flex"
									alignItems="center"
								>
									<Box
										component="span"
										color={otherUser.isOnline ? themeColor.teal : themeColor.stormGray}
										fontSize={16}
										mr={0.5}
									>
										●
									</Box>
									{otherUser.isOnline ? t('common.online') : t('common.offline')}
								</Typography>
							)}
						</Box>
					</Box>
				</Box>
				
				{/* Search button */}
				{onSearchClick && (
					<Box className="!flex !justify-self-end">
						<IconButton 
							onClick={onSearchClick} 
							aria-label="search messages"
							sx={{ 
								color: themeColor.mediumGray,
								'&:hover': {
									backgroundColor: 'rgba(0, 0, 0, 0.04)',
								}
							}}
						>
							<Icon path={mdiMagnify} size={1} />
						</IconButton>
					</Box>
				)}
				
				{/* <Box className="!flex !justify-self-end">
					<ContextMenu />
				</Box> */}
			</Box>
			{/* <Box
				className={classNames('', {
					flex: isDirectChannel || selectedChannel.channelType === ChannelType.GroupMessaging,
					hidden: !isDirectChannel && selectedChannel.channelType !== ChannelType.GroupMessaging,
				})}
			>
				<ConfirmActionButton />
			</Box> */}
		</Box>
	);
};
